import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTheme } from '../../../../theme-context';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';
import { tryToFetchAllTransactions, tryToFetchTransactionsUnreconciled } from '../../../../store/brokers/partners/transactions/transactionsSlice';
import TransactionsTable from '../tables/TransactionsTable';
import DateRangePopover from '../components/DateRange';
import RangeSlider from '../components/RangeSlider';
import SelectCheckbox from '../../../../shared/select-checkbox';
import Button from '../../../../shared/button';
import Input from '../../../../shared/input';
import Modal from '../../../../shared/modal';
import Error from '../../../../shared/error';
import TransactionsDetailsModal from './TransactionsDetailsModal';
import ReviewTransactionsModal from './ReviewTransactionsModal';


interface IUnmatchedTransactionsModal {
    openModal: boolean;
    typesData?: any;
    departmentsData?: any;
    filesTypeData?: any;
    reasonsData?: any;
    columns: any;
    selectedLatestRun?: any;
    selectedDateRange?: any;
    varianceAmountData?: any;
    handleCloseModal: () => void;
}

const UnmatchedTransactionsModal = ({
    openModal,
    typesData,
    columns,
    reasonsData,
    filesTypeData,
    departmentsData,
    selectedLatestRun,
    varianceAmountData,
    selectedDateRange,
    handleCloseModal,
}: IUnmatchedTransactionsModal) => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const csvLink = React.useRef() as React.MutableRefObject<any>;
    const [rows, setRows] = useState<any[]>([]);
    const [posDateRange, setPosDateRange] = useState<[Date, Date] | null>(null);
    const [pspDateRange, setPspDateRange] = useState<[Date, Date] | null>(null);
    const [selectedDepartment, setSelectedDepartment] = useState<any>();
    const [varianceAmount, setVarianceAmount] = useState<number>();
    const [sortSelected, setSortSelected] = useState<any>();
    const [searchText, setSearchText] = useState<string>();
    const [selectedFileType, setSelectedFileType] = useState<any>();
    const [selectedTransactions, setSelectedTransactions] = useState<any>();
    const [selectedMismatch, setSelectedMismatch] = useState<any>();
    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });
    const [showDetailsModal, setShowDetailsModal] = useState<{ show: boolean, row?: any }>();
    const [showTransactionModal, setShowTransactionModal] = useState<{ show: boolean, row?: any }>();
    const [loadingDownloadCSV, setLoadingDownloadCSV] = useState<boolean>(false)
    const [loadingDownloadExcel, setLoadingDownloadExcel] = useState<boolean>(false)
    const [downloadCSV, setDownloadCSV] = useState<any>()
    const [submitError, setSubmitError] = useState<any>();
    const transactionsData = useAppSelector(state => state.transactions.filteredTransactions?.data)
    const transactionIsLoading = useAppSelector(state => state.transactions.transactionIsLoading)
    const paginationData = useAppSelector((state) => state.transactions?.filteredTransactions?.data?.page);

    useEffect(() => {
        if (transactionsData) {
            setRows(transactionsData?.elements || []);
        }
    }, [transactionsData]);

    const onResetPaginationState = () => {
        setPaginationState({
            pageNumber: 1,
            pageSize: paginationState?.pageSize || 10,
        })
    }

    const exportToExcel = (data: string[][], fileName: string = 'data.xlsx') => {
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        saveAs(blob, fileName);
        setLoadingDownloadExcel(false)
    };

    const onGettingFilters = (download?: boolean) => {

        let settingsFilters: any = {
            data: {
                ragStatus: 'red'
            }
        }
        if (!download) {
            settingsFilters = {
                ...settingsFilters || {},
                ...paginationState,

            }
        }
        if (searchText) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    search: searchText
                }
            }
        }
        if (selectedDepartment && selectedDepartment?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    departments: selectedDepartment?.value
                }
            }
        }

        if (selectedMismatch && selectedMismatch?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    misMatch: selectedMismatch?.value
                }
            }
        }

        if (selectedLatestRun && selectedLatestRun?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    run: selectedLatestRun?.value
                }
            }
        }
        if (sortSelected && (sortSelected?.psp)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    pspDateSort: sortSelected?.psp === 'new' ? -1 : 1
                }
            }
        }
        if (sortSelected && (sortSelected?.pos)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    posDateSort: sortSelected?.pos === 'new' ? -1 : 1
                }
            }
        }
        if (sortSelected && (sortSelected?.variance)) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    varianceSort: sortSelected?.variance === 'big' ? -1 : 1
                }
            }
        }
        if (selectedTransactions && selectedTransactions?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    transactionType: selectedTransactions?.value
                }
            }
        }
        if (selectedFileType && selectedFileType?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    fileType: selectedFileType?.value
                }
            }
        }
        if (selectedDateRange && selectedDateRange?.value !== 'all') {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateRangeMin: moment(selectedDateRange?.[0])?.toISOString(),
                    dateRangeMax: moment(selectedDateRange?.[1])?.toISOString()
                }
            }
        }
        if (varianceAmount && varianceAmount !== varianceAmountData?.max) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    varianceMin: varianceAmountData?.min,
                    varianceMax: varianceAmount
                }
            }
        }
        if (posDateRange) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateFromPOS: moment(posDateRange?.[0])?.toString(),
                    dateToPOS: moment(posDateRange?.[1])?.toString()
                }
            }
        }
        if (pspDateRange) {
            settingsFilters = {
                ...settingsFilters || {},
                data: {
                    ...settingsFilters?.data || {},
                    dateFromPSP: moment(pspDateRange?.[0])?.toString(),
                    dateToPSP: moment(pspDateRange?.[1])?.toString()
                }
            }
        }
        return settingsFilters
    }

    const onDownloadData = async (type: string) => {
        if (type === 'csv') setLoadingDownloadCSV(true)
        if (type === 'excel') setLoadingDownloadExcel(true)
        try {
            setSubmitError(undefined)
            const filters: any = onGettingFilters(true)
            const formatColumns: any = []
            columns && columns?.length > 0 && columns?.map((item: any) => {
                formatColumns.push(item?.key)
                if (item?.secondKey) {
                    formatColumns?.push(item?.secondKey)
                }
                return item
            })
            const formatBody = {
                columns: formatColumns,
                ...filters?.data || {},
            }
            const response: any = await dispatch(tryToFetchAllTransactions(formatBody)).unwrap()
            if (type === 'excel') {
                exportToExcel(response, 'Unreconciled-transactions-data.xlsx')
            } else {
                setDownloadCSV(response || [])
            }
        } catch (err) {
            setSubmitError(`${err}`)
            setLoadingDownloadCSV(false)
            setLoadingDownloadExcel(false)
        }
    }

    useEffect(() => {
        if (downloadCSV && (loadingDownloadCSV)) {
            csvLink.current.link.click()
        }
        setLoadingDownloadCSV(false)
        setLoadingDownloadExcel(false)
    }, [downloadCSV])

    const onChangeSelectedFilter = (value: any, type?: string) => {
        if (type) {
            switch (type) {
                case 'mismatch':
                    setSelectedMismatch(value)
                    break;
                case 'transactions':
                    setSelectedTransactions(value)
                    break;
                case 'department':
                    setSelectedDepartment(value)
                    break;
                case 'fileType':
                    selectedFileType(value)
                    break;
            }
            onResetPaginationState();
        }
    }


    const onChangeVarianceAmount = (value: any) => {
        setVarianceAmount(value)
        onResetPaginationState();
    }

    const onCloseModal = () => {
        handleCloseModal();
    };

    const onChangeTransactionDatePOS = (value: any) => {
        setPosDateRange(value)
        onResetPaginationState()
    }

    const onChangeTransactionDatePSP = (value: any) => {
        setPspDateRange(value)
        onResetPaginationState()
    }

    const onGettingData = async () => {
        try {
            const settingsFilters = onGettingFilters()
            await dispatch(tryToFetchTransactionsUnreconciled(settingsFilters)).unwrap()
        } catch (err) {
            console.error(err)
        }
    }


    useEffect(() => {
        onGettingData()
    }, [paginationState]);

    const resetFilters = () => {
        setPosDateRange(null);
        setPspDateRange(null);
        setVarianceAmount(varianceAmountData?.max);
        setSearchText(undefined);
        setSelectedDepartment(undefined)
        setSelectedFileType(undefined)
        setSelectedMismatch(undefined)
        setSelectedTransactions(undefined)
        setShowTransactionModal(undefined)
        setShowDetailsModal(undefined)
        onResetPaginationState();
    };

    const onOpenTransactionDetails = (row: any) => {
        setShowTransactionModal(undefined)
        setShowDetailsModal({
            show: true,
            row: row
        })
    }

    const onChangeSearchValue = (e: any) => {
        setSearchText(e)
        onResetPaginationState();
    }

    const handleSortChange = (sortOrder: string, type: string) => {
        const newType = sortSelected?.[type] === sortOrder ? undefined : sortOrder
        setSortSelected((prev: any) => ({
            ...prev || {},
            [type]: newType
        }))
        onResetPaginationState();
    };

    const onReviewSuccessfully = async (reviewId?: string, newStatus?: string) => {
        const formatRows: any = (rows?.length > 0) && rows?.filter((item: any) => item?.transactionId !== reviewId)
        setRows(formatRows)
        await onGettingData()
    }

    const onClickShowTransactionModal = (transaction: any) => {
        setShowTransactionModal({
            show: true,
            row: transaction
        })
    }

    return (
        <Modal
            open={openModal}
            title='Unreconciled Transactions'
            headerStyle={`${showDetailsModal?.show || showTransactionModal?.show ? 'z-0' : ''} border-b-1 py-3 mb-0`}
            onClose={onCloseModal}
            scrollChanges={showDetailsModal?.show || showTransactionModal?.show ? '!pointer-events-none !overflow-hidden' : ''}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[85%] lg:min-w-[85%]'>
            <div className='p-2 max-w-[95vw] min-w-[95%] md:min-w-[80%] lg:min-w-[80%] min-h-[400px]'>
                <div className='my-5'>
                    <div className='bg-gray-100 dark:bg-gray-800 p-4 rounded-md shadow-md my-10'>
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                            <div>
                                <label className='block mb-1 font-medium dark:text-white'>Mismatch Reason</label>
                                <SelectCheckbox
                                    placeholder='All Reasons'
                                    options={reasonsData}
                                    selectedOption={selectedMismatch}
                                    uniqueName='mismatch'
                                    onChangeSelectedOption={onChangeSelectedFilter}
                                />
                            </div>
                            <div>
                                <label className='block mb-1 font-medium dark:text-white'>Department</label>
                                <SelectCheckbox
                                    placeholder='All Departments'
                                    options={departmentsData}
                                    selectedOption={selectedDepartment}
                                    uniqueName='department'
                                    onChangeSelectedOption={onChangeSelectedFilter}
                                />
                            </div>
                            <div>
                                <label className='block mb-1 font-medium dark:text-white'>Type</label>
                                <SelectCheckbox
                                    placeholder='All Transactions'
                                    options={typesData}
                                    selectedOption={selectedTransactions}
                                    uniqueName='transactions'
                                    onChangeSelectedOption={onChangeSelectedFilter}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4'>
                            <div className=''>
                                <DateRangePopover
                                    label='Transaction Date Range - (POS)'
                                    value={posDateRange}
                                    showTime={true}
                                    onChange={onChangeTransactionDatePOS}
                                />
                            </div>
                            <div className=''>
                                <DateRangePopover
                                    label='Transaction Date Range - (PSP)'
                                    value={pspDateRange}
                                    showTime={true}
                                    onChange={onChangeTransactionDatePSP}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-1 md:grid-cols-1 lg:grid-cols-4 gap-4 mt-4'>
                            <div>
                                <label className='block mb-1 font-medium dark:text-white'>File Type</label>
                                <SelectCheckbox
                                    placeholder='All Files'
                                    options={filesTypeData}
                                    uniqueName='fileType'
                                    selectedOption={selectedFileType}
                                    onChangeSelectedOption={onChangeSelectedFilter}
                                />
                            </div>
                            <RangeSlider
                                label='Variance Amount'
                                min={varianceAmountData?.min || 0}
                                max={varianceAmountData?.max || 0}
                                value={varianceAmount || varianceAmountData?.max || 0}
                                onChange={onChangeVarianceAmount}
                            />
                            <div className='pt-2'>
                                <Input
                                    placeholder={'Search'}
                                    containerStyle='mb-0 md:mt-5 relative dark:bg-bgColor-darkMode'
                                    inputStyle={`mb-0 border-0 focus:outline-none dark:bg-black dark:text-primary-dark`}
                                    icon={theme === 'dark' ? '/assets/navigation-icons/sidebar/search-icon-white.svg' : '/assets/shared/search.svg'}
                                    showValue={true}
                                    inputValue={searchText}
                                    inputUniqueName={'search'}
                                    onChangeInput={onChangeSearchValue}
                                    hideInputSpace={true}
                                />
                            </div>
                            <div className='flex flex-row justify-center items-center'>
                                <Button
                                    label={'Reset'}
                                    icon={<img src={theme === 'dark' ? '/assets/insights-tab/reset-white.svg' : '/assets/insights-tab/reset.svg'} className='object-contain w-[24px] mr-2' />}
                                    className='btn-primary-rounded !rounded-full !bg-white dark:!bg-black !text-[#1E4380] dark:!text-white !px-4 flex flex-row items-center justify-center mt-1'
                                    onClickButton={resetFilters}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='my-4'>
                        <div className='flex flex-col md:flex-row justify-end'>
                            <Button
                                label={loadingDownloadExcel ? 'Preparing data...' : 'Download as .xlsx'}
                                className={`${!loadingDownloadExcel ? 'btn-primary-rounded-less dark:!bg-black' : 'btn-primary-rounded-less-disable !cursor-not-allowed dark:!bg-gray-500'} md:mr-3 mb-4 md:mb-0`}
                                onClickButton={() => !loadingDownloadExcel && onDownloadData('excel')}
                            />
                            <CSVLink
                                ref={csvLink}
                                data={downloadCSV?.filter((item: any, idx: number, arr: any) => { return idx > 0 }) || []}
                                headers={downloadCSV?.[0] || []}
                                className={'none'}
                                target={'_blank'}
                                filename={`Unreconciled-transactions-data.csv`}
                            ></CSVLink>
                            <Button
                                label={loadingDownloadCSV ? 'Preparing data...' : 'Download as .csv'}
                                className={`${!loadingDownloadCSV ? 'btn-primary-rounded-less dark:!bg-black' : 'btn-primary-rounded-less-disable !cursor-not-allowed dark:!bg-gray-500'} md:mr-3`}
                                onClickButton={() => !loadingDownloadCSV && onDownloadData('csv')}
                            />
                        </div>
                        {submitError && <Error text={submitError} />}
                    </div>
                    <div>
                        <TransactionsTable
                            rows={rows}
                            columns={columns}
                            paginationData={paginationData}
                            paginationState={paginationState}
                            isLoadingMore={transactionIsLoading}
                            handleSortChange={handleSortChange}
                            setPaginationState={setPaginationState}
                            onOpenTransactionDetails={onOpenTransactionDetails}
                            onClickShowTransactionModal={onClickShowTransactionModal}
                        />
                    </div>
                </div>
                {showTransactionModal?.show &&
                    <TransactionsDetailsModal
                        openModal={showTransactionModal?.show}
                        transactionDetails={showTransactionModal?.row}
                        onOpenReview={onOpenTransactionDetails}
                        handleCloseModal={() => setShowTransactionModal(undefined)}
                    />
                }
                {showDetailsModal?.show &&
                    <ReviewTransactionsModal
                        openModal={showDetailsModal?.show}
                        mainTransaction={showDetailsModal?.row}
                        columns={columns}
                        transactions={showDetailsModal?.row?.status === 'voided transaction' ? showDetailsModal?.row?.voidedMatches : showDetailsModal?.row?.multipleReferenceMatches}
                        status={showDetailsModal?.row?.status || ''}
                        handleCloseModal={() => setShowDetailsModal(undefined)}
                        onReviewSuccessfully={onReviewSuccessfully}
                    />
                }
            </div>
        </Modal>
    );
};

export default UnmatchedTransactionsModal;